import { useEffect, useMemo, useState } from 'react';
import {
    Control,
    Controller,
    FieldError,
    FieldValues,
    Path,
    UseFormClearErrors,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import container from 'src/container';
import { OrganizationRole } from 'src/domain/organization';
import { useNotification } from 'src/components/Notification/useNotfication';
import { Error as InputError, Label as InputLabel } from './Input';
import { MultiSelectList } from './MultiSelectList';

const {
    cradle: { munduClient },
} = container;

export type CreatorRoleSelectProps<TFieldValues extends FieldValues> = {
    control: Control<TFieldValues>;
    name: Path<TFieldValues>;
    organizationId?: string;
    error?: FieldError | FieldError[];
    clearErrors?: UseFormClearErrors<TFieldValues>;
    disabled?: boolean;
};

export const CreatorRoleSelect = <TFieldValues extends FieldValues>({
    control,
    name,
    organizationId,
    error,
    clearErrors,
    disabled = false,
}: CreatorRoleSelectProps<TFieldValues>) => {
    const notify = useNotification();
    const { t } = useTranslation();
    const [roles, setRoles] = useState<OrganizationRole[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!organizationId) {
            setRoles([]);
            return;
        }

        let isMounted = true;
        const fetchRoles = async () => {
            try {
                setIsLoading(true);
                const organization = await munduClient.getOrganization(
                    organizationId,
                );
                if (isMounted) {
                    setRoles(organization.roles ?? []);
                }
            } catch (error) {
                if (error instanceof Error) {
                    notify(error.message, true);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchRoles();

        return () => {
            isMounted = false;
        };
    }, [organizationId, notify]);

    const hasRoles = roles.length > 0;
    const roleOptions = useMemo(
        () =>
            roles.map(role => ({
                label: role.name || role.id,
                value: role.id,
            })),
        [roles],
    );

    const resolvedError = Array.isArray(error) ? error[0] : error;
    const resolvedErrorMessage =
        typeof resolvedError?.message === 'string'
            ? resolvedError.message
            : undefined;

    return (
        <div>
            <InputLabel label={t('creators.form.roleLabel')} />
            {isLoading && (
                <p className="text-sm text-mongooseGray mb-1">
                    {t('common.loading')}
                </p>
            )}
            {!isLoading && organizationId && !hasRoles && (
                <p className="text-sm text-mongooseGray">
                    {t('creators.form.rolesEmpty')}
                </p>
            )}
            {organizationId && hasRoles && (
                <Controller
                    control={control}
                    name={name}
                    rules={{
                        validate: value =>
                            Array.isArray(value) && value.length > 0
                                ? true
                                : t('creators.form.roleRequired'),
                    }}
                    render={({ field }) => (
                        <MultiSelectList
                            options={roleOptions}
                            value={field.value ?? []}
                            onChange={next => {
                                field.onChange(next);
                                if (next.length) {
                                    clearErrors?.(name);
                                }
                            }}
                            disabled={disabled}
                            placeholder={t('creators.form.rolePlaceholder')}
                            noOptionsLabel={t('creators.form.rolesEmpty')}
                            selectedLabel={t('creators.form.rolePlaceholder')}
                            error={resolvedError}
                            errorMessage={resolvedErrorMessage}
                        />
                    )}
                />
            )}
            {(!organizationId || !hasRoles) && (
                <InputError error={resolvedError} />
            )}
        </div>
    );
};
